import React, {memo} from "react"
import {ClickAwayListener, Box, useTheme} from "@mui/material"
import {breakpoints} from "@next/ux-fabric"
import SearchBox from "../SearchBox"
import {AutoCompleteComponent, RecentSearchesComponent, ModalComponent} from "../../Features/SearchBar"
import connect from "./connect"
import {CloseButton, SearchArea, IconContainer, BigScreenContainer, BigScreenModalWrapper} from "./component"
import {SEARCH_DATA_GA, OPEN_SEARCH_DATA_GA, CLOSE_SEARCH_DATA_GA} from "../../config/constants"
import {PublishToModalsClosed} from "../../events/modalsClosed"
import {useMediaQuery} from "../../hooks/useMediaQuery"
import SearchDrawer from "../SearchDrawer"

export type SmallScreensProps = {
    iconUrl: string
    close: () => void
    openDrawer: () => void
    showAutoComplete: boolean
    open: boolean
    text: any
    showCloseButton: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    enableRecentSearches: boolean
}

export type DrawerProps = {
    open: boolean
    onClose: () => void
    text: any
    children: any
}

const SmallScreens = memo(
    ({
        iconUrl,
        open,
        close,
        openDrawer,
        showAutoComplete,
        text,
        showCloseButton,
        templating,
        enableRecentSearches,
    }: SmallScreensProps) => {
        const handleClick = () => {
            PublishToModalsClosed()
            openDrawer()
        }

        const theme = useTheme()
        const isMediumUp = useMediaQuery(theme.breakpoints.up("md"))
        const recentSearches = enableRecentSearches && <RecentSearchesComponent />
        return (
            <div data-testid="header-small-screen-search">
                <IconContainer
                    role="link"
                    onKeyDown={handleClick}
                    onClick={handleClick}
                    data-ga-v1={SEARCH_DATA_GA}
                    data-ga-v2={OPEN_SEARCH_DATA_GA}
                    data-testid="header-small-search-icon"
                >
                    <img src={iconUrl} alt="Search Icon" />
                </IconContainer>
                {!isMediumUp ? (
                    <SearchDrawer open={open} onClose={close} text={text} templating={templating}>
                        <SearchArea>
                            <SearchBox
                                autoFocus
                                placeholder={text.smallPlaceholder}
                                labelId="header-small-screen-search-box"
                            />
                            {showCloseButton && (
                                <CloseButton
                                    disableRipple
                                    data-testid="header-small-screen-search-close-button"
                                    onClick={close}
                                    data-ga-v1={SEARCH_DATA_GA}
                                    data-ga-v2={CLOSE_SEARCH_DATA_GA}
                                >
                                    {text.closeButton.text}
                                </CloseButton>
                            )}
                        </SearchArea>
                        {showAutoComplete ? <AutoCompleteComponent /> : recentSearches}
                    </SearchDrawer>
                ) : null}
            </div>
        )
    },
)

SmallScreens.displayName = "SmallScreens"

export type BigScreensProps = {
    open: boolean
    close: () => void
    showAutoComplete: boolean
    searchBarType: string
    bigPlaceholder: string
    isNoResults: boolean | null
}

const BigScreens = ({open, close, showAutoComplete, searchBarType, bigPlaceholder, isNoResults}: BigScreensProps) => {
    return (
        <BigScreenContainer searchbartype={searchBarType} open={open} data-testid="header-big-screen-search">
            {/* Important: Do not edit the labelId value, it is used by MVC to target the search input. */}
            <SearchBox autoFocus={false} placeholder={bigPlaceholder} labelId="header-big-screen-search-box" />
            <BigScreenModalWrapper sx={{display: {xs: "none", md: "block"}}}>
                <ModalComponent open={open} handleClose={close} hideBackdrop={showAutoComplete ? isNoResults : false}>
                    {showAutoComplete ? <AutoCompleteComponent /> : <RecentSearchesComponent />}
                </ModalComponent>
            </BigScreenModalWrapper>
        </BigScreenContainer>
    )
}

export type SearchProps = {
    openDrawer: () => void
    handleClose: () => void
    showAutoComplete: boolean
    showRecentSearch: boolean
    iconUrl: string
    searchBarType: string
    checkRecentSearch: boolean
    text: any
    isNoResults: boolean | null
    showCloseButton: boolean
    templating: {
        mainTemplate: string
        fallbackTemplate: string
    }
    enableRecentSearches: boolean
}

export const SearchBig = ({
    handleClose,
    showAutoComplete,
    showRecentSearch,
    searchBarType,
    checkRecentSearch,
    text,
    isNoResults,
}: SearchProps) => {
    const isMobile = useMediaQuery(`(max-width:${breakpoints.values.md - 0.25}px)`)
    const showPanelBigScreen = (showRecentSearch && checkRecentSearch) || (showAutoComplete && !isMobile)
    const handleCloseEvent = () => {
        if (showPanelBigScreen && !isMobile) {
            handleClose()
        }
    }
    return (
        <ClickAwayListener onClickAway={handleCloseEvent}>
            <div data-testid="header-adaptive-search-big" className="header-adaptive-search">
                <Box sx={{display: {xs: "none", md: "block"}}}>
                    <BigScreens
                        open={showPanelBigScreen}
                        close={handleCloseEvent}
                        showAutoComplete={showAutoComplete}
                        searchBarType={searchBarType}
                        bigPlaceholder={text.bigPlaceholder}
                        isNoResults={isNoResults}
                    />
                </Box>
            </div>
        </ClickAwayListener>
    )
}

export const SearchSmall = ({
    iconUrl,
    openDrawer,
    handleClose,
    showAutoComplete,
    showRecentSearch,
    text,
    showCloseButton,
    templating,
    enableRecentSearches,
}: SearchProps) => {
    const showDrawer: boolean = showAutoComplete || showRecentSearch
    const isMobile = useMediaQuery(`(max-width:${breakpoints.values.md}px)`)
    const close = () => {
        if (showDrawer && isMobile) handleClose()
    }
    return (
        <Box sx={{display: {xs: "block", md: "none"}}}>
            <div data-testid="header-adaptive-search-small" className="header-adaptive-search">
                <SmallScreens
                    iconUrl={iconUrl}
                    openDrawer={openDrawer}
                    open={showDrawer}
                    close={close}
                    showAutoComplete={showAutoComplete}
                    text={text}
                    showCloseButton={showCloseButton}
                    templating={templating}
                    enableRecentSearches={enableRecentSearches}
                />
            </div>
        </Box>
    )
}

export const SearchSmallScreen = connect(SearchSmall)
export const SearchBigScreen = connect(SearchBig)
